/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {makeStyles, RadioGroup} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import {LMUConfigSearchField} from './JobTaskLMUConfigUtils';

// defines the component style definitions ...
const useStyles = makeStyles(() => ({
  fieldContainer : {
    position      : 'absolute',
    zIndex        : 100,
    top           : '-30px',
    left          : '10px',
    display       : 'flex',
    flexDirection : 'column',
    flexWrap      : 'wrap',
    maxHeight     : '44px'
  },
  label : {
    fontSize    : '14px',
    fontWeight  : 500,
    lineHeight  : '2px',
    paddingTop  : '20px',
    marginRight : '16px'
  },
  radioGroup : {
    padding       : '0px',
    margin        : '0px',
    flexDirection : 'row'
  },
  radioRoot : {
    marginRight : '2px'
  },
  radioLabel : {
    fontSize    : '14px',
    marginLeft  : '-6px',
    marginRight : '10px'
  }
}));

/**
 * @internal
 *
 * Defines the `JobTaskLMUConfigFields` prop type definitions, which accept
 * a `defaultField` for which the search field is initially to be set, and
 * a required `onChange` prop for handling user selections of the provided
 * options.
 */
export type JobTaskLMUConfigFieldsProps = {
  onChange: (field: LMUConfigSearchField) => void;
  defaultField?: LMUConfigSearchField;
};

/**
 * @internal
 *
 * The `JobTaskLMUConfigFields` component provides the UI which displays the
 * Field to be used when searching LMU Config Files. This component displays two
 * fields, 'Version', and 'File Name', which allows the user to toggle between
 * searching files by `name` or `version`.
 */
const JobTaskLMUConfigFields = (
  {onChange, defaultField = LMUConfigSearchField.name}: JobTaskLMUConfigFieldsProps) => {
  const classes = useStyles();
  const [field, setField] = useState<LMUConfigSearchField>(defaultField);

  // invoked when the user selects the field for which searches are to be based ...
  const onFieldSelect = useCallback(({target}) => {
    const selected = target?.value;
    setField(selected);
    onChange?.(selected);
  }, []);

  // memoize the translations to avoid unnecessarily re-invoking on each render ...
  const labels = useMemo(() => (
    translate('jobcreate:search.lmuconfig', {returnObjects: true})
  ), []);

  // defines the radio label classes to be passed down as props ...
  const radioLabelClass = {
    root  : classes.radioRoot,
    label : classes.radioLabel
  };

  return (
    <section className={classes.fieldContainer}>
      <span className={classes.label}>
        {`${labels.label?.toUpperCase()}:`}
      </span>
      <RadioGroup classes={{root: classes.radioGroup}}>
        <RadioButton label={labels.version}
          value={LMUConfigSearchField.version}
          checked={field === LMUConfigSearchField.version}
          onChange={onFieldSelect}
          labelClasses={radioLabelClass}
          size='small' />
        <RadioButton label={labels.name}
          value={LMUConfigSearchField.name}
          checked={field === LMUConfigSearchField.name}
          onChange={onFieldSelect}
          labelClasses={radioLabelClass}
          size='small' />
      </RadioGroup>
    </section>
  );
};

export default React.memo(JobTaskLMUConfigFields);
